import { type ElementStyles, css, Observable, type Subscriber } from '@microsoft/fast-element';
import type { HoverIconDefinition, HoverIcon } from './HoverIcon';
import { resetFocus } from 'owa-mail-focus-manager';
import { lazyToggleRowsFlagState } from 'owa-mail-triage-action';
import {
    flagTitle,
    unflagTitle,
} from 'owa-mail-list-item-shared/lib/utils/getFlagCompleteHelper.locstring.json';
import {
    CheckmarkFilled,
    CheckmarkRegular,
    FlagFilled,
    FlagRegular,
} from '@fluentui/react-icons/fonts';
import { wrapForcedLayout } from 'owa-performance';

const FlagFilledFontGlyph = FlagFilled.codepoint;
const FlagRegularFontGlyph = FlagRegular.codepoint;
const CheckmarkFilledFontGlyph = CheckmarkFilled.codepoint;
const CheckmarkRegularFontGlyph = CheckmarkRegular.codepoint;

const UnflaggedIconStyles: ElementStyles = css`
    .icon:before {
        content: '${FlagRegularFontGlyph}';
        color: var(--colorNeutralForeground3);
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${FlagFilledFontGlyph}';
        color: var(--colorStatusDangerForeground1);
    }
`;
const FlaggedIconStyles: ElementStyles = css`
    .icon:before {
        content: '${FlagFilledFontGlyph}';
        color: var(--colorStatusDangerForeground1);
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${FlagFilledFontGlyph}';
        color: var(--customActiveDangerRedForegroundHover1);
    }
`;
const CompleteIconStyles: ElementStyles = css`
    .icon:before {
        content: '${CheckmarkRegularFontGlyph}';
        color: var(--colorNeutralForeground3);
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${CheckmarkFilledFontGlyph}';
        color: var(--colorNeutralForeground3BrandHover);
    }
`;

const subscribers = new WeakMap<Readonly<HoverIcon>, Subscriber>();

function getSubscriber(subject: Readonly<HoverIcon>): Subscriber {
    const subscriber = subscribers.get(subject) ?? {
        handleChange: applyStyles,
    };
    subscribers.set(subject, subscriber);
    return subscriber;
}

function applyStyles(subject: Readonly<HoverIcon>) {
    if (subject.isComplete) {
        subject.$fastController.removeStyles(UnflaggedIconStyles);
        subject.$fastController.removeStyles(FlaggedIconStyles);
        subject.$fastController.addStyles(CompleteIconStyles);
    } else if (subject.isFlagged) {
        subject.$fastController.removeStyles(UnflaggedIconStyles);
        subject.$fastController.addStyles(FlaggedIconStyles);
        subject.$fastController.removeStyles(CompleteIconStyles);
    } else {
        subject.$fastController.addStyles(UnflaggedIconStyles);
        subject.$fastController.removeStyles(FlaggedIconStyles);
        subject.$fastController.removeStyles(CompleteIconStyles);
    }
}

export const FlagUnflag: HoverIconDefinition<HoverIcon> = {
    title(this: Readonly<HoverIcon>) {
        return this.isComplete ? flagTitle : this.isFlagged ? unflagTitle : flagTitle;
    },
    connectedCallback(this: Readonly<HoverIcon>) {
        applyStyles(this);
        Observable.getNotifier(this).subscribe(getSubscriber(this), 'isFlagged');
        Observable.getNotifier(this).subscribe(getSubscriber(this), 'isComplete');
    },
    disconnectedCallback(this: Readonly<HoverIcon>) {
        Observable.getNotifier(this).unsubscribe(getSubscriber(this), 'isFlagged');
        Observable.getNotifier(this).unsubscribe(getSubscriber(this), 'isComplete');
    },
    onClick(this: Readonly<HoverIcon>) {
        lazyToggleRowsFlagState.importAndExecute([this.rowKey], this.tableViewId, 'Hover');
        // Reset focus after the stitch to flag the item is raised
        resetFocus('MailListItemIconBarFlagged');
    },
    onContextMenu(this: Readonly<HoverIcon>, evt: MouseEvent) {
        evt.stopPropagation();
        evt.preventDefault();
        wrapForcedLayout('FlagUnflagHoverIcon', () =>
            // eslint-disable-next-line no-restricted-properties -- We need to get the position for the menu
            this.$emit('openflagmenu', this.getBoundingClientRect())
        );
    },
};
